import { useState } from "react";
import { api, type AccessRule, type Device, type Named, type Paged, type User } from "../lib/api.js";
import { deviceId } from "../lib/format.js";
import { Drawer, Empty, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

/**
 * Permission rules decide who may connect to which machine, and what they may
 * do once connected. The capability list mirrors ControlPermissions in the
 * RustDesk protocol; the controlled machine enforces it.
 */
const CAPABILITIES: Array<{ id: string; label: string; hint: string }> = [
  { id: "keyboard", label: "Keyboard and mouse", hint: "Control the machine, not just watch it" },
  { id: "clipboard", label: "Clipboard", hint: "Copy text between the two machines" },
  { id: "file", label: "File transfer", hint: "Browse, download and upload files" },
  { id: "audio", label: "Audio", hint: "Hear the machine's sound" },
  { id: "camera", label: "Camera", hint: "View an attached camera" },
  { id: "terminal", label: "Terminal", hint: "Open a command line on the machine" },
  { id: "tunnel", label: "Port tunnel", hint: "Forward a network port" },
  { id: "restart", label: "Restart", hint: "Reboot the machine remotely" },
  { id: "recording", label: "Recording", hint: "Record the session" },
  { id: "remote_printer", label: "Remote printing", hint: "Print to a local printer" },
  { id: "block_input", label: "Block local input", hint: "Lock out the person sitting at the machine" },
  { id: "remote_modify", label: "Change settings", hint: "Modify the machine's RustDesk settings" },
  { id: "privacy_mode", label: "Privacy mode", hint: "Blank the machine's own screen" },
];

export default function AccessControl({ me, onToggleRail }: PageProps) {
  const [editing, setEditing] = useState<AccessRule | "new" | null>(null);
  const [tester, setTester] = useState(false);
  const rules = useAsync(() => api.get<AccessRule[]>("/api/console/access-rules"), []);
  const users = useAsync(() => api.get<Paged<User>>("/api/console/users?pageSize=500"), []);
  const groups = useAsync(() => api.get<Named[]>("/api/console/groups"), []);
  const deviceGroups = useAsync(() => api.get<Named[]>("/api/console/device-groups"), []);
  const devices = useAsync(() => api.get<Paged<Device>>("/api/console/devices?pageSize=500"), []);

  const canWrite = me.permissions.includes("*") || me.permissions.includes("acl:write");

  const nameOf = (r: AccessRule, side: "subject" | "object"): string => {
    if (side === "subject") {
      if (r.subject_type === "all") return "Everyone";
      if (r.subject_type === "group") {
        return (groups.data ?? []).find((g) => g.id === r.subject_id)?.name ?? "Unknown group";
      }
      const u = (users.data?.data ?? []).find((x) => x.id === r.subject_id);
      return u ? u.display_name || u.name : "Unknown person";
    }
    if (r.object_type === "all") return "All machines";
    if (r.object_type === "device_group") {
      return (deviceGroups.data ?? []).find((g) => g.id === r.object_id)?.name ?? "Unknown group";
    }
    const d = (devices.data?.data ?? []).find((x) => x.guid === r.object_id || x.id === r.object_id);
    return d ? d.hostname || deviceId(d.id) : r.object_id;
  };

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Permissions</h1>
          <span className="topbar__hint">{rules.data?.length ?? 0} rules</span>
        </div>
        <div className="topbar__actions">
          <button className="btn btn--sm" onClick={() => setTester(true)}>Test a connection</button>
          {canWrite ? (
            <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}>Add rule</button>
          ) : null}
        </div>
      </header>

      <div className="content content--flush">
        {rules.error ? <div style={{ padding: "1.25rem" }}><Notice>{rules.error}</Notice></div> : null}

        <div className="notice" style={{ margin: "1.25rem 1.25rem 0" }}>
          Connections are refused unless a rule allows them. A more specific rule wins over a
          broader one, and a block always beats an allow at the same level. The person a machine
          is assigned to always keeps full access to it.
        </div>

        {!rules.loading && !rules.data?.length ? (
          <Empty
            title="No rules yet"
            hint="Nobody but administrators and machine owners can connect until you add a rule."
            action={canWrite ? <button className="btn btn--primary" onClick={() => setEditing("new")}>Add the first rule</button> : null}
          />
        ) : (
          <div className="tablewrap" style={{ marginTop: "1rem" }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Effect</th>
                  <th>Who</th><th>Which machines</th><th>May</th>
                  <th style={{ width: 70 }} className="num">Priority</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {(rules.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className={`tag ${r.allow ? "tag--live" : "tag--deny"}`}>
                        {r.allow ? "Allow" : "Block"}
                      </span>
                    </td>
                    <td>{nameOf(r, "subject")}</td>
                    <td>{nameOf(r, "object")}</td>
                    <td className="muted small">
                      {!r.allow
                        ? <span className="faint">&mdash;</span>
                        : r.permission_names.length === CAPABILITIES.length
                          ? "Everything"
                          : r.permission_names.length === 0
                            ? "View only"
                            : r.permission_names.map((n) =>
                                CAPABILITIES.find((c) => c.id === n)?.label ?? n).join(", ")}
                    </td>
                    <td className="num faint">{r.priority}</td>
                    <td className="actions">
                      {canWrite ? <button className="btn btn--sm" onClick={() => setEditing(r)}>Edit</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <RuleDrawer
          rule={editing === "new" ? null : editing}
          users={users.data?.data ?? []}
          groups={groups.data ?? []}
          deviceGroups={deviceGroups.data ?? []}
          devices={devices.data?.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { rules.reload(); setEditing(null); }}
        />
      ) : null}

      {tester ? (
        <Tester
          users={users.data?.data ?? []}
          devices={devices.data?.data ?? []}
          onClose={() => setTester(false)}
        />
      ) : null}
    </>
  );
}

function RuleDrawer({
  rule, users, groups, deviceGroups, devices, onClose, onSaved,
}: {
  rule: AccessRule | null;
  users: User[]; groups: Named[]; deviceGroups: Named[]; devices: Device[];
  onClose: () => void; onSaved: () => void;
}) {
  const [subjectType, setSubjectType] = useState(rule?.subject_type ?? "group");
  const [subjectId, setSubjectId] = useState(rule?.subject_id ?? "");
  const [objectType, setObjectType] = useState(rule?.object_type ?? "device_group");
  const [objectId, setObjectId] = useState(rule?.object_id ?? "");
  const [allow, setAllow] = useState(rule?.allow ?? true);
  const [priority, setPriority] = useState(rule?.priority ?? 0);
  const [note, setNote] = useState(rule?.note ?? "");
  const [caps, setCaps] = useState<string[]>(
    rule?.permission_names ?? CAPABILITIES.map((c) => c.id),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setBusy(true); setError("");
    try {
      const body = {
        subject_type: subjectType, subject_id: subjectType === "all" ? "" : subjectId,
        object_type: objectType, object_id: objectType === "all" ? "" : objectId,
        allow, priority, note, permission_names: caps,
      };
      if (rule) await api.patch(`/api/console/access-rules/${rule.id}`, body);
      else await api.post("/api/console/access-rules", body);
      onSaved();
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }

  async function remove() {
    if (!rule) return;
    setBusy(true);
    try { await api.del(`/api/console/access-rules/${rule.id}`); onSaved(); }
    catch (e) { setError((e as Error).message); setBusy(false); }
  }

  const toggle = (id: string) =>
    setCaps((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <Drawer
      title={rule ? "Edit rule" : "Add rule"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save rule"}
          </button>
          <div className="spacer" />
          {rule ? <button className="btn btn--danger btn--sm" onClick={remove} disabled={busy}>Delete</button> : null}
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}

        <div className="field">
          <label className="field__label">Effect</label>
          <select value={allow ? "allow" : "block"} onChange={(e) => setAllow(e.target.value === "allow")}>
            <option value="allow">Allow the connection</option>
            <option value="block">Block the connection</option>
          </select>
        </div>

        <div className="field">
          <label className="field__label">Who this applies to</label>
          <select value={subjectType} onChange={(e) => { setSubjectType(e.target.value as any); setSubjectId(""); }}>
            <option value="all">Everyone</option>
            <option value="group">A group</option>
            <option value="user">One person</option>
          </select>
          {subjectType === "group" ? (
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} style={{ marginTop: "0.375rem" }}>
              <option value="">Choose a group</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          ) : null}
          {subjectType === "user" ? (
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} style={{ marginTop: "0.375rem" }}>
              <option value="">Choose a person</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.name}</option>)}
            </select>
          ) : null}
        </div>

        <div className="field">
          <label className="field__label">Which machines</label>
          <select value={objectType} onChange={(e) => { setObjectType(e.target.value as any); setObjectId(""); }}>
            <option value="all">All machines</option>
            <option value="device_group">A machine group</option>
            <option value="device">One machine</option>
          </select>
          {objectType === "device_group" ? (
            <select value={objectId} onChange={(e) => setObjectId(e.target.value)} style={{ marginTop: "0.375rem" }}>
              <option value="">Choose a group</option>
              {deviceGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          ) : null}
          {objectType === "device" ? (
            <select value={objectId} onChange={(e) => setObjectId(e.target.value)} style={{ marginTop: "0.375rem" }}>
              <option value="">Choose a machine</option>
              {devices.map((d) => (
                <option key={d.guid} value={d.guid}>{d.hostname || deviceId(d.id)}</option>
              ))}
            </select>
          ) : null}
        </div>

        {allow ? (
          <div className="panel">
            <div className="panel__head">
              <h3>What they may do</h3>
              <button className="btn btn--sm" onClick={() => setCaps(CAPABILITIES.map((c) => c.id))}>All</button>
              <button className="btn btn--sm" onClick={() => setCaps([])}>View only</button>
            </div>
            <div className="panel__body">
              {CAPABILITIES.map((c) => (
                <label className="check" key={c.id} title={c.hint}>
                  <input type="checkbox" checked={caps.includes(c.id)} onChange={() => toggle(c.id)} />
                  <span>{c.label}</span>
                </label>
              ))}
              <p className="small faint" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                With nothing selected the person can watch the screen but not interact.
              </p>
            </div>
          </div>
        ) : null}

        <div className="field">
          <label className="field__label">Priority</label>
          <input type="number" value={priority} min={0} max={9}
            onChange={(e) => setPriority(Number(e.target.value))} />
          <span className="field__hint">Breaks ties between equally specific rules. Higher wins.</span>
        </div>

        <div className="field">
          <label className="field__label">Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this rule exists" />
        </div>
      </div>
    </Drawer>
  );
}

function Tester({ users, devices, onClose }: { users: User[]; devices: Device[]; onClose: () => void }) {
  const [userId, setUserId] = useState("");
  const [deviceIdValue, setDeviceIdValue] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");

  async function run() {
    setError(""); setResult(null);
    try {
      setResult(await api.post("/api/console/access-rules/test", {
        user_id: userId, device_id: deviceIdValue,
      }));
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <Drawer title="Test a connection" onClose={onClose}
      footer={<button className="btn btn--primary" onClick={run} disabled={!userId || !deviceIdValue}>Check</button>}>
      <div className="stack">
        <p className="faint small">
          Check what the server would decide, without anyone having to try it.
        </p>
        {error ? <Notice>{error}</Notice> : null}
        <div className="field">
          <label className="field__label">Person</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Choose a person</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field__label">Machine</label>
          <select value={deviceIdValue} onChange={(e) => setDeviceIdValue(e.target.value)}>
            <option value="">Choose a machine</option>
            {devices.map((d) => <option key={d.guid} value={d.id}>{d.hostname || deviceId(d.id)}</option>)}
          </select>
        </div>
        {result ? (
          <Notice kind={result.allow ? "ok" : "error"}>
            <strong>{result.allow ? "Allowed" : "Refused"}</strong>
            {result.reason ? <> &mdash; {result.reason}</> : null}
            {result.allow ? (
              <div className="small" style={{ marginTop: "0.375rem" }}>
                May: {result.permission_names?.length
                  ? result.permission_names.map((n: string) =>
                      CAPABILITIES.find((c) => c.id === n)?.label ?? n).join(", ")
                  : "watch only"}
              </div>
            ) : null}
          </Notice>
        ) : null}
      </div>
    </Drawer>
  );
}
